import { Dialog, isMaxSnap, Kbd, SearchList, setThemePreference, SNAPS, useTheme, type SearchItem } from '@tomo/ui';
import {
  Columns2,
  Download,
  FilePlus2,
  FolderPlus,
  Layers,
  LayoutDashboard,
  Maximize2,
  Minimize2,
  Minus,
  Moon,
  Palette,
  PanelLeft,
  PanelRight,
  Plus,
  Settings,
  Shapes,
  Sun,
  Trash2,
  Upload,
  Vote,
  X,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useAllRecords, useBoards, useCollectionGroups, useCollections, useMeta, useProject, useReadOnly, useSession } from '../../data/hooks.tsx';
import { exportProjectArchive } from '../../data/projects.ts';
import { useLocalProjects } from '../../data/registry.ts';
import { downloadBlob, slugify } from '../../lib/files.ts';
import { Icon } from '../../lib/icons.tsx';
import { relativeTime } from '../../lib/time.ts';
import { importProjectFile } from '../projects/project-actions.ts';
import { useOpenRecord, useWindowManager, useWindows } from '../windows/hooks.ts';
import { isCompactDesk } from '../windows/windows.ts';
import { newCollectionGroup } from './collection-groups.ts';
import { addSection, useMissingSections } from './sections.tsx';
import { closePalette, openNewProject, usePalette } from './ui-state.ts';
import './palette.css';
import { newRecordLabels } from '../collections/view-utils.ts';

type Action = { item: SearchItem; run: () => void };

export function CommandPalette() {
  const { open, query } = usePalette();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && closePalette()} hideClose size="lg" align="top" className="palette" bodyClassName="palette__body">
      {open && <PaletteBody initialQuery={query} />}
    </Dialog>
  );
}

function PaletteBody({ initialQuery }: { initialQuery: string }) {
  const navigate = useNavigate();
  const session = useSession();
  const project = useProject();
  const collections = useCollections();
  const groups = useCollectionGroups();
  const records = useAllRecords();
  const boards = useBoards();
  const meta = useMeta();
  const missing = useMissingSections();
  const projects = useLocalProjects();
  const { resolved } = useTheme();
  const readOnly = useReadOnly();
  const openRecord = useOpenRecord();
  const windows = useWindowManager();
  const { wins, active, desk } = useWindows();
  const base = `/p/${session.id}`;

  const actions = useMemo<Action[]>(() => {
    const byId = new Map(collections.map((c) => [c.id, c]));
    const go = (to: string) => () => navigate(to);
    const list: Action[] = [];
    const push = (group: string, id: string, label: string, icon: ReactNode, run: () => void, extra: Partial<SearchItem> = {}) =>
      list.push({ item: { id: `${group}:${id}`, label, icon, group, ...extra }, run });

    const recent = [...records].sort((a, b) => b.updatedAt - a.updatedAt);
    for (const r of recent) {
      const c = byId.get(r.collectionId);
      if (!c) continue;
      const text = project.getRecordText(r.id).text;
      push('Records', r.id, r.title || 'Untitled', <Icon token={r.icon ?? c.icon} />, () => openRecord(r.id), {
        hint: c.name,
        description: text ? text.slice(0, 90) : undefined,
        keywords: `${c.name} ${text.slice(0, 400)}`,
      });
    }
    push('Go to', 'overview', 'Overview', <LayoutDashboard />, go(base));
    const groupName = new Map(groups.map((g) => [g.id, g.name]));
    for (const c of collections) {
      const group = groupName.get(c.groupId ?? '');
      push('Go to', c.id, c.name, <Icon token={c.icon} />, go(`${base}/c/${c.id}`), { hint: `${project.listRecords(c.id).length}`, keywords: group });
    }
    if (meta.sections.includes('boards')) {
      push('Go to', 'boards', 'Boards', <Shapes />, go(`${base}/boards`), { keywords: 'brainstorm canvas whiteboard' });
      for (const b of boards) push('Go to', `board-${b.id}`, b.name, <Shapes />, go(`${base}/boards/${b.id}`), { hint: 'Board' });
    }
    if (meta.sections.includes('votes')) push('Go to', 'votes', 'Votes', <Vote />, go(`${base}/votes`));
    push('Go to', 'trash', 'Trash', <Trash2 />, go(`${base}/trash`));
    push('Go to', 'settings', 'Project settings', <Settings />, go(`${base}/settings`));
    for (const p of projects) if (p.id !== session.id) push('Projects', p.id, p.name, <Icon token={p.icon} />, go(`/p/${p.id}`), { hint: p.cloud ? 'Cloud' : undefined });

    const labels = newRecordLabels(collections);
    if (!readOnly) collections.forEach((c, i) => {
      // The collection name stays searchable ("new units") without being spelled out.
      push('Create', `new-${c.id}`, `New ${labels[i]!.noun}`, <Plus />, () => openRecord(project.createRecord(c.id, { title: '' })), {
        hint: labels[i]!.hint,
        keywords: `new ${c.name}`,
      });
    });
    if (!readOnly) {
      if (meta.sections.includes('votes')) push('Create', 'new-poll', 'New poll', <Vote />, go(`${base}/votes?new=1`));
      if (meta.sections.includes('boards'))
        push('Create', 'new-board', 'New board', <Shapes />, () => {
          const id = project.createBoard({ name: 'Untitled board' });
          navigate(`${base}/boards/${id}`);
        });
      push('Create', 'new-collection', 'New collection…', <FilePlus2 />, () => navigate(`${base}?newCollection=1`));
      // The sidebar puts the cursor in the new group's name, so it opens there rather than here.
      push('Create', 'new-group', 'New group', <FolderPlus />, () => newCollectionGroup(project), { hint: 'A folder for collections', keywords: 'folder' });
      // The parts a project has at most one of, so they can be added without hunting for the sidebar.
      for (const s of missing)
        push(
          'Create',
          `add-${s.id}`,
          `Add ${s.label}`,
          s.icon,
          () => {
            addSection(project, s.id);
            navigate(`${base}/${s.id}`);
          },
          { hint: s.hint },
        );
    }
    push('Create', 'new-project', 'New project…', <FolderPlus />, openNewProject);

    // The window in front (if it has the keyboard), then arranging them all (not on phones, where
    // every window fills the screen).
    const front = wins.find((w) => w.id === active && !w.minimized);
    const visible = wins.filter((w) => !w.minimized).length;
    const arrange = !isCompactDesk(desk);
    if (front && arrange) {
      const max = isMaxSnap(front.snap);
      push('Windows', 'snap-left', 'Snap window left', <PanelLeft />, () => windows.place(front.id, { snap: SNAPS.left }));
      push('Windows', 'snap-right', 'Snap window right', <PanelRight />, () => windows.place(front.id, { snap: SNAPS.right }));
      push('Windows', 'maximize', max ? 'Restore window' : 'Maximize window', max ? <Minimize2 /> : <Maximize2 />, () => windows.toggleMaximize(front.id));
    }
    if (arrange && visible > 1) push('Windows', 'tile', 'Put windows side by side', <Columns2 />, () => windows.tile());
    if (arrange && visible) push('Windows', 'cascade', 'Cascade windows', <Layers />, () => windows.cascade());
    if (visible) push('Windows', 'minimize-all', 'Minimize all windows', <Minus />, () => windows.minimizeAll());
    if (wins.length) push('Windows', 'close-all', 'Close all windows', <X />, () => windows.closeAll());

    push('Actions', 'theme', resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme', resolved === 'dark' ? <Sun /> : <Moon />, () =>
      setThemePreference(resolved === 'dark' ? 'light' : 'dark'),
    );
    push('Actions', 'export', 'Export project (.tomo)', <Download />, async () => {
      const blob = await exportProjectArchive(session);
      downloadBlob(blob, `${slugify(project.getMeta().name)}.tomo`);
    });
    push('Actions', 'import', 'Import a project (.tomo)', <Upload />, () => void importProjectFile(navigate));
    push('Actions', 'design', 'Open the design system', <Palette />, go('/design'));
    return list;
  }, [collections, groups, records, boards, meta, missing, projects, project, session, navigate, base, resolved, readOnly, openRecord, windows, wins, active, desk]);

  const byId = useMemo(() => new Map(actions.map((a) => [a.item.id, a])), [actions]);
  const [query, setQuery] = useState(initialQuery);
  const items = useMemo(() => {
    const all = actions.map((a) => a.item);
    if (query.trim()) return all;
    // Empty query: only the 6 most recently edited records, then everything else.
    let n = 0;
    return all.filter((i) => i.group !== 'Records' || n++ < 6).map((i) => (i.group === 'Records' ? { ...i, group: 'Recently edited' } : i));
  }, [actions, query]);

  return (
    <SearchList
      items={items}
      query={query}
      onQueryChange={setQuery}
      placeholder="Search records, jump to a page, or run a command…"
      emptyText="Nothing matches — try another word."
      maxHeight="min(56vh, 520px)"
      limit={60}
      onSelect={(item) => {
        const a = byId.get(item.id);
        closePalette();
        a?.run();
      }}
      onEscape={closePalette}
      footer={
        <>
          <span>
            <Kbd size="sm">↑</Kbd> <Kbd size="sm">↓</Kbd> navigate
          </span>
          <span>
            <Kbd size="sm">↵</Kbd> open
          </span>
          <span>
            <Kbd size="sm">Esc</Kbd> close
          </span>
          <span style={{ marginLeft: 'auto' }}>{records.length ? `Last edit ${relativeTime(Math.max(...records.map((r) => r.updatedAt)))}` : ''}</span>
        </>
      }
    />
  );
}
